import React, {FC, useEffect, useRef} from 'react';
import cn from 'classnames';

import {renderUtils} from '@zoefin/design-system';

import DefaultStepLayout, {
  DefaultStepLayoutHideIcon,
} from '../../components/DefaultStepLayout';
import {FormFields} from '../../types';

import styles from './styles.module.scss';

type OptionsType = 'Yes' | 'No';

export interface StepHasAdvisorProps {
  hasAdvisor: FormFields['hasAdvisor'];
  hideStepIcon?: DefaultStepLayoutHideIcon;
  setHasAdvisor: (value: FormFields['hasAdvisor']) => void;
  next: () => void;
}

const HAS_ADVISOR_OPTIONS: OptionsType[] = ['Yes', 'No'];

const StepHasAdvisor: FC<StepHasAdvisorProps> = ({
  hasAdvisor,
  hideStepIcon,
  setHasAdvisor,
  next,
}) => {
  // Read only inside the change callback, never rendered — as state it cost an
  // extra render on mount to flip a flag nothing displays.
  const isMounted = useRef(false);

  renderUtils.useMount(() => {
    isMounted.current = true;
  });

  /*
   * Go to the next step the first time the user enters a value. The timeout id
   * is held in a ref because `useOnChange` has no cleanup channel — the old
   * effect returned one to cancel a pending navigation on unmount.
   */
  const advanceTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  renderUtils.useOnChange(hasAdvisor, (previousHasAdvisor) => {
    if (!isMounted.current || typeof previousHasAdvisor !== 'undefined') return;

    // Wait for the card animation to enable
    advanceTimeout.current = setTimeout(() => next(), 400);
  });

  useEffect(() => () => clearTimeout(advanceTimeout.current), []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasAdvisor(e.target.value === 'Yes');
  };

  const hasValue = typeof hasAdvisor !== 'undefined';

  return (
    <DefaultStepLayout
      icon={{
        fontAwesomeIcon: 'fa-light fa-comments',
        hidden: hideStepIcon,
      }}
      title="Let’s begin, do you currently have a financial advisor?">
      <div className={styles.hasAdvisor}>
        {HAS_ADVISOR_OPTIONS.map((key: OptionsType) => {
          const isActive =
            hasValue &&
            ((key === 'Yes' && hasAdvisor) || (key === 'No' && !hasAdvisor));

          return (
            <label
              key={key}
              htmlFor="has_advisor"
              className={cn(styles.hasAdvisor__card, {
                [styles.hasAdvisor__card__modActive]: isActive,
              })}>
              <input
                type="radio"
                name="has_advisor"
                id="has_advisor"
                value={key}
                defaultChecked={isActive}
                onChange={handleChange}
              />
              {key}
            </label>
          );
        })}
      </div>
    </DefaultStepLayout>
  );
};

export default StepHasAdvisor;
